import { proxy } from 'valtio/vanilla'
import { computed } from 'valtio-reactive'
import { cleanParams, getUrlParams } from '../persistence'
import { getSharedState } from '../valtio'

const PUBLIC_FILTERS_EXPANDED_STORAGE_KEY = 'klausPublicFiltersExpanded.v1'
const PRIVATE_FILTERS_EXPANDED_STORAGE_KEY = 'klausPrivateFiltersExpanded.v1'

export enum FilterType {
  Starred = 'starred',
  Drafts = 'drafts',
  Search = 'search',
}

// The app owns the full `FilterTimePeriod` enum (see @/modules/dashboard/types,
// consumed by conversations/api/tickets.ts). This module only compares against these two
// members, so it types `timePeriod` as a plain string instead of duplicating the
// enum — same convention as roles in the session read-model.
const TIME_PERIOD_MONTH = 'month'
const TIME_PERIOD_CUSTOM = 'custom'

// The ticket-list query parameters. Lifted from useConversationList so both Vue
// and any non-Vue consumer read one source. `timePeriod` is a string (the app
// assigns its enum).
export interface Filters {
  sort: string
  limit: number
  targetId: number
  workspace: number
  timePeriod: string
  fromDate?: string
  toDate?: string
}

export const TICKETS_PER_PAGE = 40

export const defaultFilters: Filters = {
  sort: 'newest',
  limit: TICKETS_PER_PAGE,
  targetId: 0,
  workspace: 0,
  timePeriod: TIME_PERIOD_MONTH,
}

// Minimal shape the state logic reads. The app keeps its richer `BaseFilter`
// as the public type; only `filterId`/`name` are touched here.
export interface ConversationFilter {
  filterId: number
  name: string
}

export interface ConversationFiltersState {
  filtersInitialized: boolean
  activeFilterId: number | string | null
  activeFilterName: string
  publicFilters: ConversationFilter[]
  privateFilters: ConversationFilter[]
  publicFiltersExpanded: boolean
  privateFiltersExpanded: boolean
  filterParams: Filters
}

const readExpanded = (key: string) => (localStorage[key] ? localStorage[key] === 'true' : true)

export const conversationFiltersState = getSharedState('conversation-filters', () =>
  proxy<ConversationFiltersState>({
    filtersInitialized: false,
    activeFilterId: null,
    activeFilterName: '',
    publicFilters: [],
    privateFilters: [],
    publicFiltersExpanded: readExpanded(PUBLIC_FILTERS_EXPANDED_STORAGE_KEY),
    privateFiltersExpanded: readExpanded(PRIVATE_FILTERS_EXPANDED_STORAGE_KEY),
    filterParams: { ...defaultFilters },
  }),
)

export const setPublicFiltersExpanded = (expanded = true) => {
  localStorage[PUBLIC_FILTERS_EXPANDED_STORAGE_KEY] = expanded
  conversationFiltersState.publicFiltersExpanded = expanded
}

export const setPrivateFiltersExpanded = (expanded = true) => {
  localStorage[PRIVATE_FILTERS_EXPANDED_STORAGE_KEY] = expanded
  conversationFiltersState.privateFiltersExpanded = expanded
}

// workspace is passed in explicitly (from the app session) rather than read from
// the shared-state session state, which may not be seeded yet at first reset.
export const resetFilterParams = (workspace: number) => {
  conversationFiltersState.filterParams = { ...defaultFilters, workspace }
}

// Pure transforms — exported for both frameworks and for direct testing.

// Was useConversationList.setTimeParams. Returns the next Filters; only keeps
// from/to dates when the period is custom.
export const applyTimeParams = (current: Filters, incoming: Filters): Filters => {
  if (!incoming) return current

  const timePeriod = incoming.timePeriod || current.timePeriod
  const custom = timePeriod === TIME_PERIOD_CUSTOM

  return {
    ...current,
    timePeriod,
    fromDate: custom && incoming.fromDate ? incoming.fromDate.split('T')[0] : undefined,
    toDate: custom && incoming.toDate ? incoming.toDate.split('T')[0] : undefined,
  }
}

// Was useConversationList.updateFilterParamsFromUrl. Merges the current URL query
// into the given filters and returns the next object (does not write the state).
export const mergeUrlParamsIntoFilters = (current: Filters): Filters => {
  const cleaned = cleanParams(defaultFilters, { ...getUrlParams() }) as Partial<Filters>
  if (!Object.keys(cleaned).length) return current

  let next: Filters = { ...current }
  if (cleaned.timePeriod === TIME_PERIOD_CUSTOM) next = applyTimeParams(next, cleaned as Filters)
  ;(Object.keys(next) as (keyof Filters)[]).forEach((key) => {
    if (cleaned[key]) (next as any)[key] = cleaned[key]
  })

  return next
}

const hasActiveFilter = (filters: readonly ConversationFilter[]) =>
  filters.some((filter) => filter.filterId === Number(conversationFiltersState.activeFilterId))

// The winning bundle must own both the derived proxy and its valtio-reactive
// subscriptions; another Valtio module instance cannot track the shared source.
export const conversationFiltersComputed = getSharedState('conversation-filters-computed', () =>
  computed<{
    allFilters: ConversationFilter[]
    publicFiltersActive: boolean
    privateFiltersActive: boolean
    searchModeActive: boolean
  }>({
    allFilters: () => [...conversationFiltersState.publicFilters, ...conversationFiltersState.privateFilters],
    publicFiltersActive: () => hasActiveFilter(conversationFiltersState.publicFilters),
    privateFiltersActive: () => hasActiveFilter(conversationFiltersState.privateFilters),
    searchModeActive: () => Boolean(conversationFiltersState.activeFilterId?.toString().startsWith(FilterType.Search)),
  }),
)
